import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { PlayerInfo, ServerEvent } from "../game/types"
import { bakeryStatus, fmtClock, fmtCompact, fmtInt, specLabel, toNum } from "../game/format"
import { useBakeryStore } from "../game/bakeryStore"
import { bakerLevelForXp as sharedBakerLevelForXp, bakerThresholdForLevel as sharedThreshold } from "@cookies/shared/levels"
import { usePrefs } from "../contexts/PrefsContext"
import { navigate } from "../auth/router"
import UpgradesPage from "../pages/UpgradesPage"
import QuestsPage from "../pages/QuestsPage"
import BakeryPage from "../pages/BakeryPage"
import StorePage from "../pages/StorePage"
import ProjectPage from "../pages/ProjectPage"
import RacePage from "../pages/RacePage"
import SettingsPage, { EventList, GoldenCookieOverlay } from "../pages/EventsSettingsPage"
import ProfilePage from "../pages/ProfilePage"
import CpsTester from "./CpsTester"
import Top5 from "./Top5"
import BakeScene, { BakeSceneContext } from "./BakeScene"
import { useDiscordPresence } from "../discord/presence"
import { MusicProvider } from "../contexts/MusicContext"
import { useBakeryMusic, setClickSound, sfxBuy, sfxClick, sfxFanfare, sfxJoin, sfxLeave, unlockAudioOnce } from "../game/music"
import { glowShadow, trailColor } from "../game/cosmetics"
import { FX_LUNGE_MS, FX_PRESS_MS, FX_MAX_CRUMBS, FX_MAX_FLOATERS, FX_MAX_RIPPLES, fxCrumbMs, fxCrumbsFor, fxCssVars, fxFloaterMs, fxIsOff, fxRippleMs } from "../game/fxTiming"
import { GuildBrandIcon, MuteButton, UserMenu } from "./BakeryShell/HeaderParts"
import { hoverTitle, RingAvatar } from "./BakeryShell/RosterParts"
import { GoldenFrenzyToast } from "./BakeryShell/GoldenFrenzyToast"
import { BakerySkeleton } from "./BakeryShell/Skeleton"
import { API_GRACE_MS, CHIME_THROTTLE_MS, SHELL_TICK_MS, CLICK_BOUNDS_MIN, CLICK_BOUNDS_MAX, CLICK_JITTER, CLICK_FALLBACK_RADIUS_MIN, CLICK_FALLBACK_SPREAD, CLICK_BIG_THRESHOLD, SEEN_SEQ_CAP, SEEN_SEQ_KEEP, SHELL_CPS_TOP_N } from "@cookies/shared/config"
import { useSpaceClick } from "./BakeryShell/useSpaceClick"
import { useAuth } from "../auth/useAuth"

export { BakerySkeleton }

export type BakeryTab = "bake" | "upgrades" | "quests" | "bakery" | "store" | "menu" | "project" | "race" | "profile" | "events" | "settings"

interface Floater { id: number; x: number; y: number; text: string; color: string; big: boolean }
interface Crumb { id: number; x: number; y: number; dx: number; dy: number; s: number }
interface Ripple { id: number; x: number; y: number }
interface Toast { id: number; text: string }

let fxId = 1


function thresholdForLevel(level: number): number {
  return Number(sharedThreshold(level))
}

function bakerLevelForXp(xp: number): number {
  if (!Number.isFinite(xp) || xp < 0) return 1
  return sharedBakerLevelForXp(BigInt(Math.floor(xp)))
}

import { isAppChannelFrame } from "../utils/frame";

export function BakeryShell() {
  const prefs = usePrefs()
  const { reduced, density } = prefs
  const store = useBakeryStore()
  const auth = useAuth()
  const userId = store.userId !== "" ? store.userId : (auth.user?.id ?? "")
  const username = auth.user?.username ?? "Baker"
  const onSignOut = auth.logout
  const isFrame = isAppChannelFrame()
  const snap = store.snapshot
  const connected = store.connected
  const [bootAt, setBootAt] = useState(0)
  useEffect(() => { setBootAt(Date.now()) }, [])
  const [tab, setTab] = useState<BakeryTab>("bake")
  const [eventOpen, setEventOpen] = useState(false)
  const [goldenToastSeq, setGoldenToastSeq] = useState(0)
  const [now, setNow] = useState(0)
  useEffect(() => {
    setNow(Date.now())
    const t = window.setInterval(() => setNow(Date.now()), SHELL_TICK_MS)
    return () => window.clearInterval(t)
  }, [])
  const rushEvEarly = snap?.events?.find((e) => e.id === "cookie_rush") ?? null
  const rushOnEarly = !!rushEvEarly && rushEvEarly.endsAt > now
  useBakeryMusic(rushOnEarly)
  const equippedSound = snap?.me.cosmetics?.equipped.sound ?? ""
  useEffect(() => {
    setClickSound(equippedSound)
  }, [equippedSound])
  useDiscordPresence(snap ?? null)
  const [floaters, setFloaters] = useState<Floater[]>([])
  const [crumbs, setCrumbs] = useState<Crumb[]>([])
  const [ripples, setRipples] = useState<Ripple[]>([])
  const [toasts] = useState<Toast[]>([])
  const [lunging, setLunging] = useState<string[]>([])
  const [lungeTargets, setLungeTargets] = useState<Record<string, { x: number; y: number }>>({})
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [press, setPress] = useState(false)
  const [rewardFlash] = useState<string | null>(null)
  const [hasInteracted, setHasInteracted] = useState(() => {
    try {
      return localStorage.getItem("bakery:hasInteracted") === "1"
    } catch {
      return false
    }
  })

  const lungeTimers = useRef<Map<string, number>>(new Map())
  const pressTimer = useRef<number | null>(null)
  const dismissedGoldenRef = useRef(0)
  useEffect(() => {
    const timers = lungeTimers.current
    return () => {
      for (const id of timers.values()) window.clearTimeout(id)
      timers.clear()
      if (pressTimer.current !== null) {
        window.clearTimeout(pressTimer.current)
        pressTimer.current = null
      }
    }
  }, [])
  const lunge = useCallback((uid: string, cx?: number, cy?: number) => {
    // Gate idle: only lunge when actually clicking (requires coords), not idle heartbeat
    if (reduced) return
    if (fxIsOff(density)) return
    if (cx === undefined || cy === undefined) return
    setLunging((l) => (l.includes(uid) ? l : [...l, uid]))
    if (cx !== undefined && cy !== undefined) {
      setLungeTargets((t) => ({ ...t, [uid]: { x: cx, y: cy } }))
    } else {
      setLungeTargets((t) => {
        if (!(uid in t)) return t
        const next = { ...t }
        delete next[uid]
        return next
      })
    }
    const prev = lungeTimers.current.get(uid)
    if (prev !== undefined) window.clearTimeout(prev)
    const id = window.setTimeout(() => {
      lungeTimers.current.delete(uid)
      setLunging((l) => l.filter((x) => x !== uid))
      setLungeTargets((t) => {
        if (!(uid in t)) return t
        const next = { ...t }
        delete next[uid]
        return next
      })
    }, FX_LUNGE_MS)
    lungeTimers.current.set(uid, id)
    if (cx !== undefined && cy !== undefined) {
      setLunging((l) => {
        const without = l.filter((x) => x !== uid)
        return [...without, uid]
      })
    }
  }, [reduced, density])

  const spawnClickFx = useCallback((x: number, y: number, text: string, color: string, big: boolean, _mine: boolean) => {
    // Gate idle: ripples/crumbs only onTap (actual click), not always/ background ticks
    if (fxIsOff(density)) return
    // valid click coords required — idle without tap shows no ripple/crumb
    if (!Number.isFinite(x) || !Number.isFinite(y)) return
    const id = fxId++
    setFloaters((f) => [...f.slice(-FX_MAX_FLOATERS), { id, x, y, text, color, big }])
    window.setTimeout(() => setFloaters((f) => f.filter((v) => v.id !== id)), fxFloaterMs(reduced))
    if (density !== "low") {
      const r = fxId++
      setRipples((v) => [...v.slice(-FX_MAX_RIPPLES), { id: r, x, y }])
      window.setTimeout(() => setRipples((v) => v.filter((q) => q.id !== r)), fxRippleMs(reduced))
    }
    const parts: Crumb[] = []
    const n = fxCrumbsFor(density)
    for (let i = 0; i < n; i++) {
      parts.push({
        id: fxId++,
        x, y,
        dx: (Math.random() - 0.5) * 130,
        dy: -40 - Math.random() * 90,
        s: 4 + Math.random() * 6,
      })
    }
    setCrumbs((c) => [...c.slice(-FX_MAX_CRUMBS), ...parts])
    const ids = parts.map((p) => p.id)
    window.setTimeout(() => setCrumbs((c) => c.filter((v) => !ids.includes(v.id))), fxCrumbMs(reduced))
  }, [density, reduced])

  const shown = store.shownCookies
  const credits = store.liveCredits
  const cps = snap ? toNum(snap.bakery.cps) : 0
  const power = snap ? toNum(snap.me.clickPower) || 1 : 1
  const cookiesPerClick = store.cookiesPerClick
  const creditsPerClick = store.creditsPerClick
  const clickBreakdown = store.clickBreakdown
  const players: PlayerInfo[] = snap?.players ?? []
  const active = players.filter((p) => p.active)
  const selfLoadout = snap?.me.cosmetics?.equipped
  const withSelfCos = (pl: PlayerInfo): PlayerInfo =>
    pl.userId === userId && selfLoadout ? { ...pl, cosmetics: { ...selfLoadout } } : pl
  const rushEv = snap?.events?.find((e) => e.id === "cookie_rush") ?? null
  const ev = rushEv ?? snap?.events?.[0] ?? null
  const endsIn = rushEv ? rushEv.endsAt - now : ev ? ev.endsAt - now : 0
  const rushOn = !!rushEv && endsIn > 0
  const guildName = snap?.guild?.name && snap.guild.name.trim() !== "" ? snap.guild.name : "Bakery"
  const st = bakeryStatus(active.length, rushOn, shown)
  const me = players.find((p) => p.userId === userId) ?? null
  const hoveredPlayer = hoveredId ? players.find((p) => p.userId === hoveredId) ?? null : null
  const hoveredBoard = hoveredId ? snap?.leaderboard?.find((e) => e.userId === hoveredId) ?? null : null
  const selfBoard = snap?.leaderboard?.find((e) => e.userId === userId) ?? null
  const selfContrib = (() => {
    const fromMe = snap?.me?.contribution !== undefined ? toNum(snap.me.contribution) : 0
    if (fromMe > 0) return fromMe
    const fromPlayer = me ? toNum(me.contribution ?? "0") : 0
    if (fromPlayer > 0) return fromPlayer
    return selfBoard ? toNum(selfBoard.contribution) : 0
  })()
  const panelPlayer = hoveredPlayer ?? hoveredBoard ?? null
  const quietHintVisible = st.cls === "quiet" && !hasInteracted && shown === 0

  const doClick = (cx: number, cy: number) => {
    if (!hasInteracted) {
      setHasInteracted(true)
      try {
        localStorage.setItem("bakery:hasInteracted", "1")
      } catch {
        return
      }
    }
    store.click(cx, cy)
    unlockAudioOnce()
    sfxClick()
    setPress(true)
    if (pressTimer.current !== null) window.clearTimeout(pressTimer.current)
    pressTimer.current = window.setTimeout(() => {
      setPress(false)
      pressTimer.current = null
    }, FX_PRESS_MS)
    lunge(userId, cx, cy)
  }

  const doSneaky = useCallback((sid: string, cx: number, cy: number) => {
    store.clickSneaky(sid, cx, cy)
    sfxFanfare()
    spawnClickFx(cx, cy, "✨", "#ffd23f", true, true)
    lunge(userId, cx, cy)
  }, [store, spawnClickFx, lunge, userId])

  const doMini = useCallback((cid: string, cx: number, cy: number) => {
    store.clickMini(cid, cx, cy)
    sfxClick()
    spawnClickFx(cx, cy, "✨", "#ffd23f", true, true)
    lunge(userId, cx, cy)
  }, [store, spawnClickFx, lunge, userId])

  const doClickRef = useRef(doClick)
  useEffect(() => {
    doClickRef.current = doClick
  })
  useSpaceClick(prefs.spaceClick === true, tab, doClickRef)

  useEffect(() => {
    const f = store.sneakyFlash
    if (!f) return
    const text = "+" + f.amount + " 🍪 +" + f.credits + " ✨"
    spawnClickFx(f.x, f.y, text, "#ffd23f", true, true)
    lunge(userId, f.x, f.y)
  }, [store.sneakyFlash, spawnClickFx, lunge, userId])

  useEffect(() => {
    const f = store.miniFlash
    if (!f) return
    const text = "+" + f.amount + " 🍪 +" + f.credits + " ✨ (x" + f.multiplier + ")"
    spawnClickFx(f.x, f.y, text, "#ffd23f", true, true)
    lunge(userId, f.x, f.y)
    sfxFanfare()
  }, [store.miniFlash, spawnClickFx, lunge, userId])

  const goldenEvRaw = snap?.events?.find((e) => e.id === "golden_cookie") ?? null
  const goldenEv = goldenEvRaw && goldenEvRaw.endsAt > now ? goldenEvRaw : null
  const goldenEndsAt = goldenEv?.endsAt
  useEffect(() => {
    if (goldenEv !== null) {
      if (dismissedGoldenRef.current === goldenEv.endsAt) return
      setEventOpen(true)
    }
  }, [goldenEv, goldenEndsAt])
  const closeGolden = useCallback(() => {
    if (goldenEv !== null) dismissedGoldenRef.current = goldenEv.endsAt
    setEventOpen(false)
  }, [goldenEv])
  const claimGolden = useCallback(() => {
    if (goldenEv === null) return
    dismissedGoldenRef.current = goldenEv.endsAt
    const gx = typeof goldenEv.x === "number" ? goldenEv.x : 0.5
    const gy = typeof goldenEv.y === "number" ? goldenEv.y : 0.5
    store.redeemGolden(gx, gy)
    sfxFanfare()
    spawnClickFx(gx, gy, "🌟", "#ffd23f", true, true)
    lunge(userId, gx, gy)
    setEventOpen(false)
    setTab("bake")
  }, [goldenEv, store, spawnClickFx, lunge, userId])

  // Space accepts golden cookie popup when open (pref-independent) and navigates to Bake tab
  useEffect(() => {
    const isSpace = (e: KeyboardEvent): boolean => e.code === "Space" || e.key === " " || e.key === "Spacebar"
    const isEnter = (e: KeyboardEvent): boolean => e.key === "Enter"
    const inEditable = (t: EventTarget | null): boolean => {
      if (!(t instanceof HTMLElement)) return false
      const tag = t.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true
      if (t.isContentEditable) return true
      return false
    }
    const onDown = (e: KeyboardEvent): void => {
      if (goldenEv === null || !eventOpen) return
      if (!isSpace(e) && !isEnter(e)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (inEditable(e.target)) return
      e.preventDefault()
      try {
        if (typeof (e as unknown as { stopPropagation?: () => void }).stopPropagation === "function") {
          ;(e as unknown as { stopPropagation: () => void }).stopPropagation()
        }
      } catch {
      }
      if (e.repeat) return
      claimGolden()
    }
    window.addEventListener("keydown", onDown, true)
    return () => window.removeEventListener("keydown", onDown, true)
  }, [goldenEv, eventOpen, claimGolden])

  const goldenFlash = store.goldenFlash
  useEffect(() => {
    if (!goldenFlash) return
    setGoldenToastSeq(goldenFlash.seq)
    const text = "+" + goldenFlash.amount + " 🍪 +" + goldenFlash.credits + " ✨"
    spawnClickFx(goldenFlash.x, goldenFlash.y, text, "#ffd23f", true, true)
    lunge(userId, goldenFlash.x, goldenFlash.y)
  }, [goldenFlash, spawnClickFx, lunge, userId])

  const chimeAtRef = useRef(0)
  useEffect(() => {
    const unsub = store.subscribe((e: ServerEvent) => {
      if (e.type === "upgrade_purchased" || e.type === "module_upgraded" || e.type === "project_completed" || e.type === "spec_chosen") {
        const eid = (e as unknown as { userId?: string }).userId
        if (eid === undefined || eid === userId) sfxBuy()
        return
      }
      if (e.type === "event_started") {
        sfxFanfare()
        return
      }
      if (e.type === "player_joined" || e.type === "player_left") {
        const nowMs = Date.now()
        if (nowMs - chimeAtRef.current < CHIME_THROTTLE_MS) return
        const eid = e.type === "player_joined" ? e.player.userId : e.userId
        if (eid === userId) return
        chimeAtRef.current = nowMs
        if (e.type === "player_joined") sfxJoin()
        else sfxLeave()
        return
      }
    })
    return () => unsub()
  }, [store, userId])

  const seenSeqRef = useRef<Set<number>>(new Set())
  useEffect(() => {
    const unsub = store.subscribe((e: ServerEvent) => {
      if (e.type !== "click") return
      if (e.type === "click" && e.userId === userId) {
        const selfCred = typeof e.creditsGained === "string" && e.creditsGained !== "" ? toNum(e.creditsGained) : Math.ceil(toNum(e.amount) / 2)
        const goldenActive = snap?.events?.some((ev) => ev.id === "golden_cookie" && ev.endsAt > now) ?? false
        const trail = snap?.me.cosmetics?.equipped.trail ?? ""
        spawnClickFx(e.x, e.y, "+" + e.amount + " 🍪 +" + selfCred + " ✨", trailColor(trail, goldenActive ? "#ffd23f" : "#f5b942"), toNum(e.amount) >= CLICK_BIG_THRESHOLD, true)
        return
      }
      if (e.seq !== 0 && seenSeqRef.current.has(e.seq)) return
      if (e.seq !== 0) {
        seenSeqRef.current.add(e.seq)
        if (seenSeqRef.current.size > SEEN_SEQ_CAP) {
          const keep = Array.from(seenSeqRef.current).slice(-SEEN_SEQ_KEEP)
          seenSeqRef.current = new Set(keep)
        }
      }
      const snapPlayers = store.snapshot?.players ?? []
      const pl = (e as { player?: PlayerInfo }).player
      const found = snapPlayers.find((p) => p.userId === e.userId)
      const color = pl?.color ?? found?.color ?? "#38bdf8"
      let cx = e.x
      let cy = e.y
      const valid = Number.isFinite(cx) && Number.isFinite(cy) && cx >= 0 && cx <= 1 && cy >= 0 && cy <= 1
      if (!valid) {
        const ang = Math.random() * Math.PI * 2
        const rad = CLICK_FALLBACK_RADIUS_MIN + Math.random() * CLICK_FALLBACK_SPREAD
        cx = 0.5 + Math.cos(ang) * rad
        cy = 0.52 + Math.sin(ang) * rad
      } else {
        cx = Math.min(CLICK_BOUNDS_MAX, Math.max(CLICK_BOUNDS_MIN, cx + (Math.random() - 0.5) * CLICK_JITTER))
        cy = Math.min(CLICK_BOUNDS_MAX, Math.max(CLICK_BOUNDS_MIN, cy + (Math.random() - 0.5) * CLICK_JITTER))
      }
      const remoteCred = typeof e.creditsGained === "string" && e.creditsGained !== "" ? toNum(e.creditsGained) : Math.ceil(toNum(e.amount) / 2)
      const txt = "+" + e.amount + " 🍪 +" + remoteCred + " ✨"
      const big = toNum(e.amount) >= CLICK_BIG_THRESHOLD
      spawnClickFx(cx, cy, txt, color, big, false)
      lunge(e.userId, cx, cy)
    })
    return () => unsub()
  }, [store, userId, spawnClickFx, lunge, snap])

  const cpsBreakdown = useMemo(() => {
    if (!snap) return [] as Array<{ name: string; detail: string }>
    const bakeryCps = toNum(snap.bakery.cps ?? "0")
    const rows: Array<{ name: string; detail: string }> = [{ name: "Bakery", detail: `+${fmtCompact(bakeryCps)}/s` }]
    const sorted = [...snap.players]
      .map((p) => ({ p, cps: toNum((p as unknown as { cps?: string }).cps ?? "0") }))
      .filter((x) => x.cps > 0)
      .sort((a, b) => b.cps - a.cps)
    const top = sorted.slice(0, SHELL_CPS_TOP_N)
    for (const { p, cps } of top) {
      rows.push({ name: p.name, detail: `+${fmtCompact(cps)}/s` })
    }
    if (sorted.length > SHELL_CPS_TOP_N) {
      const rest = sorted.slice(SHELL_CPS_TOP_N).reduce((acc, x) => acc + x.cps, 0)
      rows.push({ name: `Rest (${sorted.length - SHELL_CPS_TOP_N} bakers)`, detail: `+${fmtCompact(rest)}/s` })
    }
    return rows
  }, [snap])

  const navTab = tab === "project" || tab === "race" ? "bakery" : tab === "profile" || tab === "events" || tab === "settings" ? "menu" : tab
  const bakerXp = snap ? toNum(snap.me.xp ?? snap.me.totalClicks ?? "0") : 0
  const bakerLevel = snap?.me.level ?? (snap ? 1 : bakerLevelForXp(bakerXp))
  const bakerNext = snap?.me.nextLevelXp !== undefined ? toNum(snap.me.nextLevelXp) : thresholdForLevel(bakerLevel + 1)
  const bakerBase = thresholdForLevel(bakerLevel)
  const bakerSpan = Math.max(1, bakerNext - bakerBase)
  const bakerPct = Math.max(0, Math.min(100, ((bakerXp - bakerBase) / bakerSpan) * 100))
  const bakeryBankNum = snap ? toNum(snap.bakery.cookies) : 0
  const bakeryLifetimeNum = snap ? toNum(snap.bakery.lifetimeCookies ?? snap.bakery.cookies) : 0
  const bakeryLevelNum = snap?.bakery.level ?? 1
  const bakeryNextNum = snap?.bakery.nextLevelCookies !== undefined ? toNum(snap.bakery.nextLevelCookies) : 15000
  const bakeryPctLive = snap?.bakery.levelProgress !== undefined ? Math.max(0, Math.min(100, toNum(snap.bakery.levelProgress))) : 0
  const inGrace = snap === null && now - bootAt < API_GRACE_MS
  const showError = store.error !== null && !inGrace
  const sceneValue = useMemo(() => ({ press, ripples, crumbs, floaters, lunging, lungeTargets, onTap: doClick, onSneakyClick: doSneaky, onMiniClick: doMini }), [press, ripples, crumbs, floaters, lunging, lungeTargets, doClick, doSneaky, doMini])

  return (
    <MusicProvider rush={rushOnEarly}>
    <div className={"bk-root" + (reduced ? " rm" : "")} style={fxCssVars()}>
      <header className="bk-header">
        {isFrame ? (
          <div className="bk-brand">
            <GuildBrandIcon guildId={snap?.guild?.id ?? ""} icon={snap?.guild?.icon} name={guildName} />
            <span className="bk-brand-text">
              <span className="bk-brand-name">{guildName}</span>
              <span className="bk-brand-xp-row">
                <span className="bk-brand-xp" title={`Baker Lv ${bakerLevel} · ${fmtInt(Math.floor(bakerXp))}/${fmtInt(bakerNext)} Baker XP (your clicks) → Baker level`}>
                  <span className="bk-xp-track" role="progressbar" aria-valuenow={Math.floor(bakerPct)} aria-valuemin={0} aria-valuemax={100} aria-label={`Baker level ${bakerLevel}, ${Math.floor(bakerXp)} of ${bakerNext} Baker XP from clicks`}><span className="bk-xp-fill" style={{ width: `${Math.floor(bakerPct)}%` }} /><span className="bk-xp-text">Baker Lv {bakerLevel} · {fmtCompact(bakerXp)}/{fmtCompact(bakerNext)} XP</span></span>
                </span>
                <span className="bk-xp-div" aria-hidden="true" />
                <span className="bk-brand-xp bakery" title={`Bakery Lv ${bakeryLevelNum} · ${fmtInt(Math.floor(bakeryLifetimeNum))}/${fmtInt(bakeryNextNum)} lifetime baked (bank ${fmtInt(Math.floor(bakeryBankNum))}) → Bakery level`}>
                  <span className="bk-xp-track" role="progressbar" aria-valuenow={Math.floor(bakeryPctLive)} aria-valuemin={0} aria-valuemax={100} aria-label={`Bakery level ${bakeryLevelNum}, ${Math.floor(bakeryLifetimeNum)} of ${bakeryNextNum} lifetime baked`}><span className="bk-xp-fill" style={{ width: `${Math.floor(bakeryPctLive)}%` }} /><span className="bk-xp-text">Bakery Lv {bakeryLevelNum} · {fmtCompact(bakeryLifetimeNum)}/{fmtCompact(bakeryNextNum)}</span></span>
                </span>
              </span>
            </span>
          </div>
        ) : (
          <button type="button" className="bk-brand" onClick={() => navigate("/")} aria-label="Go home" title="Go home">
            <GuildBrandIcon guildId={snap?.guild?.id ?? ""} icon={snap?.guild?.icon} name={guildName} />
            <span className="bk-brand-text">
              <span className="bk-brand-name">{guildName}</span>
              <span className="bk-brand-xp-row">
                <span className="bk-brand-xp" title={`Baker Lv ${bakerLevel} · ${fmtInt(Math.floor(bakerXp))}/${fmtInt(bakerNext)} Baker XP (your clicks) → Baker level`}>
                  <span className="bk-xp-track" role="progressbar" aria-valuenow={Math.floor(bakerPct)} aria-valuemin={0} aria-valuemax={100} aria-label={`Baker level ${bakerLevel}, ${Math.floor(bakerXp)} of ${bakerNext} Baker XP from clicks`}><span className="bk-xp-fill" style={{ width: `${Math.floor(bakerPct)}%` }} /><span className="bk-xp-text">Baker Lv {bakerLevel} · {fmtCompact(bakerXp)}/{fmtCompact(bakerNext)} XP</span></span>
                </span>
                <span className="bk-xp-div" aria-hidden="true" />
                <span className="bk-brand-xp bakery" title={`Bakery Lv ${bakeryLevelNum} · ${fmtInt(Math.floor(bakeryLifetimeNum))}/${fmtInt(bakeryNextNum)} lifetime baked (bank ${fmtInt(Math.floor(bakeryBankNum))}) → Bakery level`}>
                  <span className="bk-xp-track" role="progressbar" aria-valuenow={Math.floor(bakeryPctLive)} aria-valuemin={0} aria-valuemax={100} aria-label={`Bakery level ${bakeryLevelNum}, ${Math.floor(bakeryLifetimeNum)} of ${bakeryNextNum} lifetime baked`}><span className="bk-xp-fill" style={{ width: `${Math.floor(bakeryPctLive)}%` }} /><span className="bk-xp-text">Bakery Lv {bakeryLevelNum} · {fmtCompact(bakeryLifetimeNum)}/{fmtCompact(bakeryNextNum)}</span></span>
                </span>
              </span>
            </span>
          </button>
        )}
        <div className="bk-cookie-pill" title={`Bakery Lv ${bakeryLevelNum} · ${fmtInt(Math.floor(bakeryLifetimeNum))}/${fmtInt(bakeryNextNum)} lifetime baked · bank ${fmtInt(Math.floor(bakeryBankNum))} → Bakery level`}>
          <span className="num">🍪 {fmtCompact(shown)}</span>
          <span className="cps-wrap" tabIndex={0} aria-label="Bakery breakdown">
            <span className="cps">+{fmtCompact(cps)}/sec</span>
            <span className="bk-cps-tip" role="tooltip">
              <span className="bk-cps-tip-title">Bakery breakdown</span>
              {cpsBreakdown.length === 0 && <span className="bk-cps-row">No buildings yet</span>}
              {cpsBreakdown.map((r) => (
                <span key={r.name} className="bk-cps-row">
                  <span className="bk-cps-name">{r.name}</span>
                  <span className="bk-cps-val">{r.detail}</span>
                </span>
              ))}
            </span>
          </span>
        </div>
        <div className="bk-head-right">
          {(rushOn || goldenEv !== null || (!connected && snap !== null)) && (
            <span className={"bk-event-chip" + (rushOn ? " rush" : "")} role="status" aria-live="polite">
              {rushOn ? "🔥 " + fmtClock(endsIn) : goldenEv !== null ? `🌟 Golden ${fmtClock(Math.max(0, goldenEv.endsAt - now))}` : "🟡 reconnecting…"}
            </span>
          )}
          <div className="bk-self">
            <MuteButton />
            <span className="bk-xp-wrap" tabIndex={0} aria-label={`Baker level ${bakerLevel}, ${Math.floor(bakerXp)} of ${bakerNext} Baker XP from clicks`} title={`Baker Lv ${bakerLevel} · ${fmtInt(Math.floor(bakerXp))}/${fmtInt(bakerNext)} Baker XP (your clicks)`}>
              <span className="bk-credits">✨ {fmtInt(credits)}</span>
              <span className="bk-xp-tip" role="tooltip">
                <span className="bk-cps-tip-title">Baker Lv {bakerLevel} · {fmtInt(Math.floor(bakerXp))}/{fmtInt(bakerNext)} Baker XP (clicks)</span>
                <span className="bk-xp-track"><span className="bk-xp-fill" style={{ width: `${Math.floor(bakerPct)}%` }} /></span>
                <span className="bk-cps-row">{fmtInt(Math.max(0, bakerNext - Math.floor(bakerXp)))} Baker XP to Lv {bakerLevel + 1}</span>
              </span>
            </span>
            <UserMenu username={username} onSignOut={onSignOut} />
          </div>
        </div>
      </header>

      {showError && (
        <div className="bk-reconnect" role="alert">
          {store.error}{" "}
          <button type="button" className="bk-authbtn" style={{ marginLeft: 8 }} onClick={store.retry}>Retry</button>
          {!isFrame && (
            <button type="button" className="bk-authbtn" style={{ marginLeft: 8 }} onClick={() => navigate("/")}>
              Back to bakeries
            </button>
          )}
        </div>
      )}
      {!connected && store.error === null && snap !== null && (
        <div className="bk-reconnect warn" role="status" aria-live="polite">
          Connection interrupted · Reconnecting…{" "}
          <button type="button" className="bk-authbtn" style={{ marginLeft: 8 }} onClick={store.retry}>Retry now</button>
        </div>
      )}
      {!snap && store.error === null && (
        <div className="bk-reconnect info" role="status" aria-live="polite">Loading bakery…</div>
      )}

      {tab === "bake" && (
        <main className="bk-main">
          <aside className="bk-side left" aria-label="Active bakers">
            <div className="bk-side-title">Active Bakers<span className="bk-side-count">{active.length} online</span></div>
            <ul className="bk-roster">
              {active.map((p, idx) => {
                const board = snap?.leaderboard?.find((e) => e.userId === p.userId) ?? null
                const lvl = p.level ?? board?.level ?? 1
                const pow = p.clickPower ?? board?.clickPower ?? "1"
                const cos = withSelfCos(p)
                const glow = glowShadow(cos.cosmetics?.glow)
                return (
                <li
                  key={p.userId}
                  title={hoverTitle(p.name, board?.specialization ?? p.spec, toNum(p.contribution ?? "0"), toNum(p.cps ?? board?.cps ?? "0"), pow, lvl)}
                  className={(p.userId === userId ? "me" + (rushOn ? " rushing" : "") : "") + " hoverable" + (hoveredId === p.userId ? " hovered" : "")}
                  onMouseEnter={() => setHoveredId(p.userId)}
                  onMouseLeave={() => setHoveredId((v) => (v === p.userId ? null : v))}
                  onFocus={() => setHoveredId(p.userId)}
                  onBlur={() => setHoveredId((v) => (v === p.userId ? null : v))}
                  tabIndex={0}
                >
                  <span className="bk-ava-wrap" aria-hidden="true"><RingAvatar player={cos} lazy={idx >= 5} /><span className="bk-lvlbadge">Lv {lvl}</span></span>
                  <span className="bk-rname" style={glow ? { textShadow: glow } : undefined}>{p.name}</span>
                  <span className="bk-rspec">{specLabel(p.spec)}</span>
                </li>
                )
              })}
            </ul>
            <div className={"bk-quiet" + (quietHintVisible ? " visible" : "")} aria-hidden={!quietHintVisible}>The bakery is quiet. Start baking to wake it up!</div>
          </aside>

          <section className="bk-stage" aria-label="Bakery oven">
            <div className={"bk-status " + st.cls} role="status" aria-live="polite">● {st.label}</div>
            <BakeSceneContext.Provider value={sceneValue}>
              <BakeScene />
            </BakeSceneContext.Provider>
            <div className="bk-hint" title={clickBreakdown}>Click the cookie for +{fmtCompact(cookiesPerClick)} 🍪 (+{fmtCompact(creditsPerClick)} ✨){prefs.spaceClick ? " · Space also bakes (discrete presses)" : ""}{me ? " · baking as " + me.name : ""}</div>
            <div className="bk-toasts" aria-live="polite">
              {toasts.map((t) => (<div key={t.id} className="bk-toast">{t.text}</div>))}
              {rewardFlash && <div className="bk-toast gold">{rewardFlash}</div>}
              {goldenFlash && goldenFlash.seq === goldenToastSeq && (
                <GoldenFrenzyToast amount={goldenFlash.amount} credits={goldenFlash.credits} frenzyEndsAt={goldenFlash.frenzyEndsAt} onDone={() => setGoldenToastSeq(0)} />
              )}
            </div>
          </section>

          <aside className="bk-side right" aria-label="Bakery status">
            {rushOn ? (
              <div className={"bk-event-card active" + (endsIn < 9000 ? " soon" : "")}>
                <div className="bk-evtitle">🔥 Cookie Rush</div>
                <div className="bk-evtime">{fmtClock(endsIn)} remaining</div>
                <div className="bk-evbonus">+10% cookies<br />+25% click effects</div>
              </div>
            ) : goldenEv !== null ? (
              <div className="bk-event-card active">
                <div className="bk-evtitle">🌟 Golden Frenzy</div>
                <div className="bk-evtime">{fmtClock(Math.max(0, goldenEv.endsAt - now))} remaining</div>
                <div className="bk-evbonus">5x cookies for 10s<br />Keep clicking the cookie</div>
              </div>
            ) : rewardFlash ? (
              <div className="bk-event-card done">
                <div className="bk-evtitle">🎉 Event complete!</div>
                <div className="bk-evbonus">{rewardFlash}</div>
              </div>
            ) : (
              <div className="bk-event-card calm">
                <div className="bk-evtitle">🏠 Bakery is calm</div>
                <div className="bk-evbonus">No active event<br />Keep baking to trigger one.</div>
              </div>
            )}
            <div className="bk-mecard">
              {panelPlayer && panelPlayer.userId !== userId ? (
                <>
                  <div className="bk-metitle">👤 {hoveredBoard?.name ?? hoveredPlayer?.name ?? "Baker"} · {specLabel(hoveredBoard?.specialization ?? hoveredPlayer?.spec ?? "Baker")}</div>
                  <div className="bk-merow"><span>Contribution</span><b>{fmtCompact(Math.max(toNum(hoveredPlayer?.contribution ?? "0"), hoveredBoard ? toNum(hoveredBoard.contribution) : 0))}</b></div>
                  <div className="bk-merow"><span>CPS</span><b>+{fmtCompact(hoveredPlayer?.cps ?? hoveredBoard?.cps ?? "0")}/s</b></div>
                  <div className="bk-merow"><span>Click power</span><b>{fmtCompact(hoveredPlayer?.clickPower ?? hoveredBoard?.clickPower ?? "1")}</b></div>
                  <div className="bk-merow"><span>Level</span><b>Lv {hoveredPlayer?.level ?? hoveredBoard?.level ?? 1}</b></div>
                </>
              ) : (
                <>
                  <div className="bk-metitle">👤 {me?.name ?? selfBoard?.name ?? "You"} · {specLabel(selfBoard?.specialization ?? me?.spec ?? "Baker")}</div>
                  <div className="bk-merow"><span>Contribution</span><b>{fmtCompact(selfContrib)}</b></div>
                  <div className="bk-merow"><span>CPS</span><b>+{fmtCompact(me?.cps ?? selfBoard?.cps ?? "0")}/s</b></div>
                  <div className="bk-merow"><span>Click power</span><b>{fmtCompact(String(power))}</b></div>
                  <div className="bk-merow"><span>Level</span><b>Lv {bakerLevel} · {fmtInt(Math.floor(bakerXp))}/{fmtInt(bakerNext)} XP</b></div>
                </>
              )}
            </div>
          </aside>
        </main>
      )}

      {tab !== "bake" && (
        <main className="bk-main bk-sub">
          <div className="bk-wide">
            {tab === "upgrades" && <UpgradesPage />}
            {tab === "quests" && <QuestsPage />}
            {tab === "bakery" && <BakeryPage go={setTab} />}
            {tab === "store" && <StorePage />}
            {tab === "project" && <ProjectPage />}
            {tab === "race" && <RacePage />}
            {tab === "events" && <EventList />}
            {tab === "settings" && <SettingsPage />}
            {tab === "profile" && <ProfilePage />}
            {tab === "menu" && (
              <div className="bk-menu-pages">
                <div className="bk-side-title" style={{ color: "var(--dark-brown)" }}>Menu</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                  <button type="button" className="bk-authbtn" onClick={() => setTab("project")}>🏗 Project</button>
                  <button type="button" className="bk-authbtn" onClick={() => setTab("race")}>🏆 Race</button>
                  <button type="button" className="bk-authbtn" onClick={() => setTab("events")}>🎪 Events</button>
                  <button type="button" className="bk-authbtn" onClick={() => setTab("profile")}>👨‍🍳 Profile</button>
                  <button type="button" className="bk-authbtn" onClick={() => setTab("settings")}>⚙ Settings</button>
                </div>
                {!connected && (
                  <div className="bk-merow" style={{ color: "var(--dark-brown)" }}><span>Disconnected</span><button type="button" className="bk-authbtn" onClick={store.retry}>Reconnect</button></div>
                )}
                <div style={{ marginTop: 12 }}>
                  <CpsTester />
                </div>
                <div style={{ marginTop: 12 }}>
                  <Top5 />
                </div>
              </div>
            )}
          </div>
        </main>
      )}

      <nav className="bk-nav" aria-label="Bakery sections">
        {(() => {
          const questBadge = store.claimableCount > 0 ? String(store.claimableCount) : ""
          const items: Array<[BakeryTab, string, string, string]> = [["bake", "🍪", "Bake", ""], ["upgrades", "⚙️", "Upgrades", credits >= 120 ? "!" : ""], ["quests", "📜", "Quests", questBadge], ["bakery", "🏭", "Bakery", ev ? "•" : ""], ["store", "✨", "Store", ""], ["menu", "☰", "Menu", ""]]
          return items.map((n) => (
            <button type="button" key={n[0]} className={"bk-navbtn" + (navTab === n[0] ? " on" : "")} aria-current={navTab === n[0] ? "page" : undefined} onClick={() => setTab(n[0])}>
              <span className="bk-nico" aria-hidden="true">{n[1]}</span>
              <span>{n[2]}</span>
              {n[3] !== "" && <span className="bk-badge">{n[3]}</span>}
            </button>
          ))
        })()}
      </nav>
      {eventOpen && goldenEv !== null && (
        <GoldenCookieOverlay
          endsAt={goldenEv.endsAt}
          onClaim={claimGolden}
          onClose={closeGolden}
        />
      )}
      {store.offlineBonus && (
        <div className="cb-modal-bg" role="dialog" aria-modal="true">
          <div className="cb-modal">
            <h3>While you were away</h3>
            <div style={{ fontSize: 14, lineHeight: 1.4 }}>
              +{fmtInt(toNum(store.offlineBonus.cookies))} cookies +{fmtInt(toNum(store.offlineBonus.credits))} credits ({store.offlineBonus.hours} hours{(store.offlineBonus.rate ?? 0) > 0 ? ` @ ${fmtInt(store.offlineBonus.rate ?? 0)}/hr` : ""})
            </div>
            <div className="cb-modal-row">
              <button type="button" className="cb-buy" onClick={store.clearOfflineBonus}>Collect</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </MusicProvider>
  )
}
